import { ReactNode, useEffect } from 'react'
import { useLocation, useParams } from 'react-router'
import queryString from 'query-string'

import useQuoteStore from './store'
import { FlowContext, useFlowContext } from 'directFlow/utils/FlowContext'

import { useGetQuoteV2Query } from 'app/services'
import { transformQuoteV2ToPageState } from 'utils/transformStateToDetailsList'

export const QuoteStoreProvider = ({ children }: { children: ReactNode }) => {
  const store = useQuoteStore()
  const { quoteId } = useParams<{ quoteId: string }>()
  const flowContext = useFlowContext()
  const location = useLocation()

  const isStartQuote = location.pathname.includes('start')

  const query = queryString.parse(location.search) as {
    referralCode?: string
    utm_source?: string
  }

  if (!quoteId) {
    throw new Error('QuoteStoreProvider was used on a route without a quote ID')
  }

  const existingQuote = store.getQuoteData(quoteId)

  const { data: quoteData } = useGetQuoteV2Query(
    { quoteId },
    {
      skip: flowContext === FlowContext.DIRECT,
    },
  )

  useEffect(() => {
    /*
      TODO: when we want to swtich to the Zustand store implementation completely we should then
      uncomment this code:

      const navigate = useNavigate()

      if(!existingQuote && !isStartQuote) {
        navigate('/start-quote')
      }

      This will redirect us back to the stat-quote page whenever we access a route without a valid quote
      from the store
    */

    if (!quoteId) return

    if (flowContext === FlowContext.DIRECT && isStartQuote && !existingQuote) {
      store.createQuote(quoteId, {
        generalInfo: {
          referralCode: query.referralCode,
          utmSource: query.utm_source,
        },
      })
    }

    if (flowContext === FlowContext.POST_QUOTE && quoteData && !existingQuote) {
      const transformedQuoteData = transformQuoteV2ToPageState({
        data: quoteData,
        showPreviousInsuranceCancelled: true,
      })

      store.createEditQuote(quoteId, { pageState: transformedQuoteData })
    }
  }, [quoteId, existingQuote, isStartQuote, quoteData])

  return <>{children}</>
}
